/**
 * ============================================================================
 *  File        : frontend/src/hooks/usePermissions.ts
 *  Module      : hooks
 *  Purpose     : RBAC 權限判定 hook — 讀 AuthContext 的 roles / permissions
 *  Author      : EF Engineering
 *  Created     : 2026-05-31
 *  Updated     : 2026-05-31
 *  License     : Proprietary - ECO FAITH Platform v3.0
 * ============================================================================
 */

import { useMemo } from 'react'
import { useAuth } from '../context/AuthContext'
import { ALL_ACCESS_ROLES } from '../lib/permissions'

export interface PermissionsApi {
  roles: string[]
  permissions: string[]
  /** 是否為全存取角色（super_admin / platform_admin）。 */
  isAllAccess: boolean
  /** 是否擁有指定權限；null/undefined 視為公開（登入即可）。 */
  can: (permission?: string | null) => boolean
  /** 是否擁有任一指定權限。 */
  canAny: (permissions: readonly string[]) => boolean
}

export function usePermissions(): PermissionsApi {
  const { roles, permissions } = useAuth()

  return useMemo<PermissionsApi>(() => {
    const isAllAccess = roles.some((role) => ALL_ACCESS_ROLES.includes(role))
    const granted = new Set(permissions)
    const can = (permission?: string | null): boolean => {
      if (permission === null || permission === undefined) return true
      if (isAllAccess) return true
      return granted.has(permission)
    }
    return {
      roles,
      permissions,
      isAllAccess,
      can,
      canAny: (required) => required.some((permission) => can(permission)),
    }
  }, [roles, permissions])
}
